import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PatientService } from '../../services';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../services';
import { instantMs } from '../../alarms-api';

@Component({
  selector: 'app-alarm-preview',
  templateUrl: './alarm-preview.component.html',
  styleUrls: ['./alarm-preview.component.scss']
})
export class AlarmPreviewComponent implements OnInit {
  @Input() alarm: any;
  @Output() close = new EventEmitter<void>();

  alarmDetails: any = null;
  patientCard: any = null;
  notes: any[] = [];
  allNotesVisible = false;
  isLoading = true;

  // Extra note
  showNoteForm = false;
  noteForm: FormGroup;
  isSavingNote = false;
  private currentUser: any;

  // Total persisted length cap for `extraNote` column on the backend.
  // Header (date/author) and `\n\n` separator between notes count toward this budget.
  private readonly NOTE_TOTAL_LIMIT = 5000;
  private readonly NOTE_MIN_LENGTH = 2;
  private notePrefix = '';
  private noteSeparator = '';
  maxNoteTextLength = this.NOTE_TOTAL_LIMIT;

  constructor(
    private patientService: PatientService,
    private translate: TranslateService,
    private fb: FormBuilder,
    private userService: UserService
  ) {
    this.noteForm = this.fb.group({
      extraNote: ['', [Validators.required, Validators.minLength(4)]]
    });
  }

  ngOnInit() {
    if (this.alarm) {

      const dateMs = instantMs(this.alarm.date);
      if (!this.alarm.timeCome && dateMs !== null) {
        this.alarm.timeCome = new Date(dateMs).toISOString();
      }

      if (!this.alarm.notifType && this.alarm.discr) {
        this.alarm.notifType = this.alarm.discr === 'alarmleavezone' ? 'zone' : this.alarm.discr;
      }

      if (!this.alarm.firstName && this.alarm.name) {
        const parts = this.alarm.name.trim().split(' ');
        this.alarm.firstName = parts[0] || '';
        this.alarm.lastName = parts.slice(1).join(' ') || '';
      }

      this.loadAlarmData();
    }
    this.currentUser = this.userService.getCurrentUser();
  }

  loadAlarmData() {
    this.isLoading = true;

    this.patientService.getAlarmPreview(
      this.alarm.imei,
      this.alarm.timeCome,
      this.alarm.notifType
    ).subscribe(
      (response) => {
        if (response && response.data) {
          this.alarmDetails = response.data;
        }
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      }
    );

    this.patientService.getDiseases(this.alarm.imei).subscribe(
      (data) => {
        if (data && data.data) {
          this.patientCard = data.data;
        }
      },
      () => {}
    );

    this.patientService.getBandNote(this.alarm.imei).subscribe(
      (data) => {
        if (data?.note) {
          this.notes = this.parseNotes(data.note);
        }
      },
      () => {}
    );

  }

  parseNotes(noteString: string): any[] {
    if (!noteString) return [];
    const noteBlocks = noteString.split('\n---\n');
    return noteBlocks.map(block => {
      const lines = block.trim().split('\n');
      const metaLine = lines[0] || '';
      const metaMatch = metaLine.match(/^\[(.+?)\]\s*(.+)/);
      return {
        date: metaMatch ? metaMatch[1] : '',
        author: metaMatch ? metaMatch[2] : '',
        text: lines.slice(1).join('\n').trim()
      };
    }).filter(n => n.text);
  }

  getAlarmTypeLabel(): string {
    switch (this.alarm.notifType) {
      case 'sos': return this.translate.instant('DASHBOARD.ALARM_SOS');
      case 'fall': return this.translate.instant('DASHBOARD.FALL');
      case 'zone': return this.translate.instant('DASHBOARD.ALARM_ZONE');
      case 'immo': return this.translate.instant('AI-ANALYSIS.IMMOBILITY');
      default: return this.translate.instant('DASHBOARD.ALARM_SOS');
    }
  }

  getAlarmTypeTranslation(): string {
    if (this.alarmDetails?.alarmType) {
      const typeMap = {
        'normal': 'SOS_REQUEST.ALARM_TYPE_OPTION_NORMAL',
        'normalNotif': 'SOS_REQUEST.ALARM_TYPE_OPTION_DEFAULT',
        'normal112': 'SOS_REQUEST.ALARM_TYPE_OPTION_112',
        'false': 'SOS_REQUEST.ALARM_TYPE_OPTION_FALSE',
        'technical': 'SOS_REQUEST.ALARM_TYPE_OPTION_TECHNICAL',
        'learning': 'SOS_REQUEST.ALARM_TYPE_OPTION_LEARNING',
        'advice': 'SOS_REQUEST.ALARM_TYPE_OPTION_ADVICE',
        'other': 'SOS_REQUEST.ALARM_TYPE_OPTION_OTHER'
      };
      const key = typeMap[this.alarmDetails.alarmType];
      if (key) {
        return this.translate.instant(key);
      }
      return this.alarmDetails.alarmType;
    }
    return '-';
  }

  getDiseaseTags(): string[] {
    if (!this.patientCard) return [];
    const diseases: string[] = [];
    if (this.patientCard.nadcisnienie) diseases.push(this.translate.instant('SOS_REQUEST.HYPERTENSION'));
    if (this.patientCard.parkinson) diseases.push(this.translate.instant('SOS_REQUEST.PARKINSON'));
    if (this.patientCard.alzheimer) diseases.push(this.translate.instant('SOS_REQUEST.ALZHEIMER'));
    if (this.patientCard.cukrzyca) diseases.push(this.translate.instant('SOS_REQUEST.DIABETES'));
    if (this.patientCard.astma) diseases.push(this.translate.instant('SOS_REQUEST.ASTHMA'));
    if (this.patientCard.arytmia) diseases.push(this.translate.instant('SOS_REQUEST.ARRHYTHMIA'));
    if (this.patientCard.tarczyca) diseases.push(this.translate.instant('SOS_REQUEST.THYROID'));
    if (this.patientCard.padaczka) diseases.push(this.translate.instant('SOS_REQUEST.EPILEPSY'));
    if (this.patientCard.po_zawale) diseases.push(this.translate.instant('SOS_REQUEST.AFTER_HEART_ATTACK'));
    return diseases;
  }

  get displayedNotes(): any[] {
    return this.allNotesVisible ? this.notes : this.notes.slice(0, 2);
  }

  // --- Extra note ---

  openNoteForm() {
    const existingLen = this.alarmDetails?.description?.extraNote?.length || 0;
    this.noteSeparator = existingLen > 0 ? '\n\n' : '';
    this.notePrefix = this.buildNotePrefix();

    this.maxNoteTextLength = Math.max(
      0,
      this.NOTE_TOTAL_LIMIT - existingLen - this.noteSeparator.length - this.notePrefix.length
    );

    this.showNoteForm = true;
    this.noteForm.reset();
    const ctrl = this.noteForm.get('extraNote');
    ctrl?.setValidators([
      Validators.required,
      Validators.minLength(this.NOTE_MIN_LENGTH),
      Validators.maxLength(this.maxNoteTextLength)
    ]);
    ctrl?.updateValueAndValidity();
    ctrl?.markAsUntouched();
  }

  private buildNotePrefix(): string {
    const user = this.currentUser?.user_id;
    const author = user ? `${user['first_name'] || ''} ${user['last_name'] || ''}`.trim() : '';
    return `${this.formatNoteTimestamp(new Date())} - ${author}\n`;
  }

  // Fixed "YYYY-MM-DD HH:mm:ss" instead of toLocaleString(), which renders differently
  // per browser/OS locale (dots, commas, AM/PM) and isn't safely reparseable later.
  private formatNoteTimestamp(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
      + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  get remainingNoteChars(): number {
    const typed = (this.noteForm.get('extraNote')?.value || '').length;
    return this.maxNoteTextLength - typed;
  }

  get noteLimitReached(): boolean {
    const existingLen = this.alarmDetails?.description?.extraNote?.length || 0;
    const sepLen = existingLen > 0 ? 2 : 0;
    const prefixLen = this.buildNotePrefix().length;
    return this.NOTE_TOTAL_LIMIT - existingLen - sepLen - prefixLen < this.NOTE_MIN_LENGTH;
  }

  cancelNoteForm() {
    this.showNoteForm = false;
    this.noteForm.reset();
  }

  saveExtraNote() {
    if (this.noteForm.invalid || this.isSavingNote) return;

    this.isSavingNote = true;
    const noteText = this.noteForm.get('extraNote')?.value;
    const stampedNote = `${this.notePrefix}${noteText}`;

    // descId: try alarmDetails first, then alarm input, then description
    const descId = this.alarmDetails?.descId;

    if (!descId) {
      console.error('AlarmPreview: descId not found', {
        alarmDetails: this.alarmDetails,
        alarm: this.alarm
      });
      this.isSavingNote = false;
      return;
    }

    const payload = { descId, extraNote: stampedNote };

    this.patientService.addExtraNoteToAlarm(payload).subscribe(
      (response) => {
        this.onNoteSaved(stampedNote);
      },
      (error) => {
        // API returns plain text, Angular fails to parse as JSON — treat as success
        if (error.status === 200 || error.error instanceof SyntaxError) {
          this.onNoteSaved(stampedNote);
        } else {
          console.error('AlarmPreview: save failed', error);
          this.isSavingNote = false;
        }
      }
    );
  }

  private onNoteSaved(stampedNote: string) {
    if (!this.alarmDetails) {
      this.alarmDetails = { description: {} };
    }
    if (!this.alarmDetails.description) {
      this.alarmDetails.description = {};
    }

    this.alarmDetails.description.extraNote = this.alarmDetails.description.extraNote
      ? this.alarmDetails.description.extraNote + '\n\n' + stampedNote
      : stampedNote;

    this.showNoteForm = false;
    this.isSavingNote = false;
    this.noteForm.reset();
  }

  closePreview() {
    this.close.emit();
  }

  onOverlayClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('alarm-preview-overlay')) {
      this.closePreview();
    }
  }

  get extraNoteCount(): number {
    const note = this.alarmDetails?.description?.extraNote;
    if (!note) return 0;
    return note.split(/\n\n/).filter(block => block.trim()).length;
  }

}
